import { createReducer, on } from '@ngrx/store';
import { AuthState, initialAuthState } from './auth.state';
import * as AuthActions from './auth.actions';

/**
 * Auth Reducer
 *
 * Handles authentication flow state (loading, errors).
 * User data is handled by user.reducer listening to loginSuccess/logout.
 */
export const authReducer = createReducer(
	initialAuthState,

	// Login started
	on(
		AuthActions.login,
		(state): AuthState => ({
			...state,
			loading: true,
			error: null,
		})
	),

	// Login completed successfully
	on(
		AuthActions.loginSuccess,
		(state): AuthState => ({
			...state,
			loading: false,
			error: null,
		})
	),

	// Login failed (invalid credentials, account locked, etc.)
	on(
		AuthActions.loginFailure,
		(state, { error }): AuthState => ({
			...state,
			loading: false,
			error,
		})
	),

	// Logout - tokens are cleared from localStorage immediately (no API wait)
	// Preserve returnUrl so user can be redirected after re-login
	on(
		AuthActions.logout,
		(state): AuthState => ({
			...initialAuthState,
			loading: false,
			returnUrl: state.returnUrl,
		})
	),

	// Initialize session started
	on(
		AuthActions.initializeSession,
		(state): AuthState => ({
			...state,
			loading: true,
			error: null,
		})
	),

	// Initialize session succeeded
	on(
		AuthActions.initializeSessionSuccess,
		(state): AuthState => ({
			...state,
			loading: false,
			error: null,
		})
	),

	// Initialize session failed (no valid session)
	on(
		AuthActions.initializeSessionFailure,
		(state, { error }): AuthState => ({
			...state,
			loading: false,
			error,
		})
	),

	// Set return URL (called by AuthGuard when unauthenticated user tries to access protected route)
	on(
		AuthActions.setReturnUrl,
		(state, { returnUrl }): AuthState => {
			console.log('[Auth Reducer] setReturnUrl received:', {
				oldReturnUrl: state.returnUrl,
				newReturnUrl: returnUrl
			});
			return {
				...state,
				returnUrl,
			};
		}
	),

	// Clear return URL (called after successful navigation)
	on(
		AuthActions.clearReturnUrl,
		(state): AuthState => ({
			...state,
			returnUrl: null,
		})
	)
);
